import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1554079501-b418a64a2543?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxkb2clMjBwYXJrJTIwbmF0dXJlJTIwdHJlZXN8ZW58MHx8fHwxNzgxNzQxNjA5fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      toast.success(`Bienvenue, ${data.user.first_name} !`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="Parc canin" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1411]/90 via-[#0F1411]/30 to-transparent" />
        <div className="absolute bottom-0 p-12 text-white max-w-lg">
          <h2 className="font-heading text-4xl font-bold tracking-tight">
            Entretien des parcs canins, simplifié.
          </h2>
          <p className="mt-3 text-white/80 text-lg">
            Planifiez, assignez et suivez chaque entretien depuis un seul calendrier.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm space-y-7 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="space-y-2">
            <img src="/logo.png" alt="ParcCanin" className="h-14 w-14 rounded-2xl object-contain" />
            <h1 className="font-heading text-3xl font-bold tracking-tight pt-2">Connexion</h1>
            <p className="text-muted-foreground text-sm">
              Identifiez-vous pour accéder à votre calendrier d'entretiens.
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Courriel</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@exemple.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pr-10"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          <Button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full active:scale-95 transition-transform"
            size="lg"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Se connecter
          </Button>
        </form>
      </div>
    </div>
  );
}
